import moment from "moment";

import { PayableInboxFilterSettings } from "@/Billing/Models/Settings/PayableInboxFilterSettings";
import { ISalesInboxModel } from "@/Billing/Models/UI/ISalesInboxModel";
import { ITransactionsInboxModel } from "@/Billing/Models/UI/ITransactionsInboxModel";
import { PayableInboxModel } from "@/Billing/Models/UI/PaybleInboxModel";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { BillingInbox } from "@/Common/Models/UI/Enums";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";

class BillingAction extends BaseAction {
    private helper: MyleHelper = new MyleHelper();

    public OpenSelectedBill(privateBillId: string): void {
        var requestData = {
            PrivateBillId: privateBillId
        };
        this.Execute(ServerOperations.Server.Myle_GetBillingSelectedData, requestData);
    }

    public LoadPatient(patientId: string, subOp: number): void {
        var requestData =
        {
            InactivableId: patientId,
            IncludeRelatedRecalls: false
        };

        this.Execute(ServerOperations.Server.Myle_LoadPatient, requestData, subOp);
    }

    public LoadSalesData(Model: ISalesInboxModel): void {
        this.Execute(ServerOperations.Server.Myle_LoadSalesData, this.GetSalesRequestData(Model));
    }

    public LoadTransactionsData(Model: ITransactionsInboxModel): void {
        this.Execute(ServerOperations.Server.Myle_LoadTransactionsData, this.GetTransactionsRequestData(Model));
    }

    public async PrintTransactionsReport(model: ITransactionsInboxModel, columnsToShow: string[]): Promise<void> {
        var requestData = this.GetTransactionsRequestData(model);
        requestData["BillingInboxType"] = BillingInbox.Transactions;
        requestData["ColumnsToShow"] = columnsToShow;
        if (this.ValidateBillingReportPrintingDates(requestData.FromDate && moment(requestData.FromDate).toDate(), requestData.ToDate && moment(requestData.ToDate).toDate())) {
            await this.ExecuteAsync(ServerOperations.Server.Myle_Billing_GenerateReportPDF, requestData);
        }
    }

    public async PrintSalesReport(Model: ISalesInboxModel, columnsToShow: string[]): Promise<void> {
        var requestData = this.GetSalesRequestData(Model);
        requestData["BillingInboxType"] = BillingInbox.Sales;
        requestData["ColumnsToShow"] = columnsToShow;
        if (this.ValidateBillingReportPrintingDates(requestData.FromDate && moment(requestData.FromDate).toDate(), requestData.ToDate && moment(requestData.ToDate).toDate())) {
            await this.ExecuteAsync(ServerOperations.Server.Myle_Billing_GenerateReportPDF, requestData);
        }
    }

    public async PrintPayablesReport(model: PayableInboxModel, columnsToShow: string[]): Promise<void> {
        const requestData = this.GetPayableRequestData(model, columnsToShow);
        if (this.ValidateBillingReportPrintingDates(requestData.PayableBillFilterSettings.CreationDateFrom, requestData.PayableBillFilterSettings.CreationDateTo)) {
            await this.ExecuteAsync(ServerOperations.Server.Myle_Billing_GeneratePayableBillsReportPdf, requestData);
        }
    }

    private ValidateBillingReportPrintingDates(fromDate: Date, toDate: Date): boolean {
        const errorMessage = ValidationHelper.validateSearchDateRangeMax5Years(fromDate, toDate);

        if (errorMessage) {
            sessionStore.showPopup(errorMessage);
            return false;
        }
        return true;
    }

    private GetSalesRequestData(Model: ISalesInboxModel): SearchRequestData {
        var requestData = {
            FromDate: null,
            ToDate: null,
            VisitFromDate: null,
            VisitToDate: null,
            SiteIds: [],
            ProviderIds: [],
            BilledByIds: [],
            IsClosed: false,
            IsOpen: false,
            IsInvalid: false,
            IsValid: false,
            PaymentMethods: [],
            SearchText: "",
            PageNumber: 0,
            PageSize: 0
        };

        // Remove TimeZone, since this is Timezone Agnostic and relative to the time of the clinic
        if (Model.get("DateFrom") != null)
            requestData.FromDate = moment(Model.get("DateFrom")).format("YYYY/MM/DD 00:00:00");

        if (Model.get("DateTo") != null)
            requestData.ToDate = moment(Model.get("DateTo")).format("YYYY/MM/DD 00:00:00");

        if (Model.get("VisitFromDate") != null)
            requestData.VisitFromDate = moment(Model.get("VisitFromDate")).format("YYYY/MM/DD 00:00:00");

        if (Model.get("VisitToDate") != null)
            requestData.VisitToDate = moment(Model.get("VisitToDate")).format("YYYY/MM/DD 00:00:00");

        requestData.SearchText = Model.get("GlobalSalesSearchText");
        requestData.PageSize = Model.get("SelectedPageSizeOption");
        requestData.PageNumber = Model.get("SelectedPageNumber");

        requestData.ProviderIds = this.helper
            .GetCheckedItemsFromICheckableList(Model.get("ProviderFiltered"))
            .toArray()
            .filter(x => x.Checked)
            .map(x => (<EmployeeUIModel>x).Model.ContactFacet_id);

        requestData.BilledByIds = this.helper
            .GetCheckedItemsFromICheckableList(Model.get("BilledByFiltered"))
            .toArray()
            .filter(x => x.Checked)
            .map(x => (<EmployeeUIModel>x).Model.ContactFacet_id);

        if (Model.get("ShowOnlySelectedSites"))
            requestData.SiteIds = this.helper
                .GetCheckedItemsFromICheckableList(Model.get("Sites"))
                .toArray()
                .filter(x => x.Checked)
                .map(x => x.Id);
        else if (Model.get("Sites") != null) {
            requestData.SiteIds = Model.get("Sites")
                .toArray()
                .map(x => x.Id);
        }

        if (Model.get("Status") != null) {
            Model.get("Status").forEach(status => {
                if (status.Id === "closed_status")
                    requestData.IsClosed = status.Checked;
                if (status.Id === "open_status")
                    requestData.IsOpen = status.Checked;
            });
        }

        if (Model.get("Validity") != null) {
            Model.get("Validity").forEach(validity => {
                if (validity.Id === "valid")
                    requestData.IsValid = validity.Checked;
                if (validity.Id === "invalid")
                    requestData.IsInvalid = validity.Checked;
            });
        }

        requestData.PaymentMethods = this.helper
            .GetCheckedItemsFromICheckableList(Model.get("PaymentMethod"))
            .toArray().filter(x => x.Checked)
            .map(x => x.Id);

        return requestData;
    }

    private GetTransactionsRequestData(Model: ITransactionsInboxModel): SearchRequestData {
        var requestData = {
            FromDate: null,
            ToDate: null,
            VisitFromDate: null,
            VisitToDate: null,
            SiteIds: [],
            ProviderIds: [],
            BilledByIds: [],
            IsClosed: false,
            IsOpen: false,
            IsInvalid: false,
            IsValid: false,
            PaymentMethods: [],
            SearchText: "",
            PageNumber: 0,
            PageSize: 0,
            PaymentFromDate: null,
            PaymentToDate: null
        };

        if (Model.get("DateFrom") != null) {
            requestData.FromDate = moment(Model.get("DateFrom")).format("YYYY/MM/DD 00:00:00");
        }

        if (Model.get("DateTo") != null) {
            requestData.ToDate = moment(Model.get("DateTo")).format("YYYY/MM/DD 00:00:00");
        }

        if (Model.get("VisitDateFrom") != null)
            requestData.VisitFromDate = moment(Model.get("VisitDateFrom")).format("YYYY/MM/DD 00:00:00");

        if (Model.get("VisitDateTo") != null)
            requestData.VisitToDate = moment(Model.get("VisitDateTo")).format("YYYY/MM/DD 00:00:00");

        requestData.SearchText = Model.get("GlobalTransactionsSearchText");
        requestData.PageSize = Model.get("SelectedPageSizeOption");
        requestData.PageNumber = Model.get("SelectedPageNumber");

        requestData.ProviderIds = this.helper
            .GetCheckedItemsFromICheckableList(Model.get("ProviderFiltered"))
            .toArray()
            .filter(x => x.Checked)
            .map(
                x => (<EmployeeUIModel>x).Model.ContactFacet_id
            );

        requestData.BilledByIds = this.helper
            .GetCheckedItemsFromICheckableList(Model.get("BilledByFiltered"))
            .toArray()
            .filter(x => x.Checked)
            .map(x => (<EmployeeUIModel>x).Model.ContactFacet_id);

        if (Model.get("ShowOnlySelectedSites")) {
            requestData.SiteIds = this.helper
                .GetCheckedItemsFromICheckableList(Model.get("Sites"))
                .toArray()
                .filter(x => x.Checked)
                .map(x => x.Id);
        }
        else if (Model.get("Sites") != null) {
            requestData.SiteIds = Model.get("Sites")
                .toArray()
                .map(x => x.Id);
        }

        if (Model.get("Status") != null) {
            Model.get("Status").forEach(status => {
                if (status.Id === "closed_status")
                    requestData.IsClosed = status.Checked;
                if (status.Id === "open_status")
                    requestData.IsOpen = status.Checked;
            });
        }

        if (Model.get("Validity") != null) {
            Model.get("Validity").forEach(validity => {
                if (validity.Id === "valid")
                    requestData.IsValid = validity.Checked;
                if (validity.Id === "invalid")
                    requestData.IsInvalid = validity.Checked;
            });
        }

        requestData.PaymentMethods = this.helper
            .GetCheckedItemsFromICheckableList(Model.get("PaymentMethod"))
            .toArray()
            .filter(x => x.Checked)
            .map(x => x.Id);

        requestData.PaymentFromDate = Model.get("PaymentDateFrom");
        requestData.PaymentToDate = Model.get("PaymentDateTo");

        return requestData;
    }

    private GetPayableRequestData(Model: PayableInboxModel, columnsToShow: string[]): GetPayableRequestData {
        const filterSettings = Model?.filterSettings;
        const payableBillFilterSettings = new PayableInboxFilterSettings();

        if (filterSettings?.CreationDateFrom)
            payableBillFilterSettings.CreationDateFrom = filterSettings?.CreationDateFrom;

        if (filterSettings?.CreationDateTo)
            payableBillFilterSettings.CreationDateTo = filterSettings?.CreationDateTo;

        if (filterSettings?.VisitDateFrom)
            payableBillFilterSettings.VisitDateFrom = filterSettings?.VisitDateFrom;

        if (filterSettings?.VisitDateTo)
            payableBillFilterSettings.VisitDateTo = filterSettings.VisitDateTo;

        payableBillFilterSettings.Pagination = filterSettings?.Pagination;

        payableBillFilterSettings.CliniciansProviderContactFacetIds = filterSettings?.CliniciansProviderContactFacetIds;

        payableBillFilterSettings.CliniciansBilledByContactFacetIds = filterSettings?.CliniciansBilledByContactFacetIds;

        payableBillFilterSettings.SiteIds = filterSettings?.SiteIds;

        payableBillFilterSettings.ValidityEnums = filterSettings?.ValidityEnums;

        payableBillFilterSettings.SearchText = filterSettings?.SearchText;

        return {
            ColumnsToShow: columnsToShow,
            PayableBillFilterSettings: payableBillFilterSettings
        };
    }
}

interface SearchRequestData {
    FromDate: string;
    ToDate: string;
    VisitFromDate: string;
    VisitToDate: string;
    SiteIds: string[];
    ProviderIds: string[];
    BilledByIds: string[];
    IsClosed: boolean;
    IsOpen: boolean;
    IsInvalid: boolean;
    IsValid: boolean;
    PaymentMethods: string[];
    SearchText: string;
    PageNumber: number;
    PageSize: number;
}

interface GetPayableRequestData {
    ColumnsToShow: string[];
    PayableBillFilterSettings: PayableInboxFilterSettings;
}

export { BillingAction };
